import {
    createContext, useContext, useState, useEffect, FC, ReactNode,
    SetStateAction, Dispatch
} from "react";
import getConfig from "next/config";
import { useRouter } from "next/router";
import { GroupListItem } from "@themuse/design-system/lib/components/GroupList/GroupList";
import { formatLog } from "@themuse/design-system";

import {
    companyIndustryItems,
    companySizeItems,
    diversityInclusionItems,
    experienceLevelItems,
    jobCategoryItems,
    postedDateRangeItems,
    searchRadiusItems
} from "utils/searchFacetItems";
import { generateUrlPath } from "utils/browser";
import { normalizeLocationName } from "utils/strings";
import { searchResultsSelector } from "selectors";
import {
    SearchFilters, SearchKeyword, SearchCompany, SearchLocation
} from "interfaces/context/searchContextInterfaces";
import { PerkAndBenefitCategory } from "interfaces/context/searchFacetsContextInterfaces";
import { SearchHistoryEntry, SearchHistoryContextType } from "../interfaces/context/searchHistoryInterfaces";
import { useSearchState } from "./SearchContext";
import { useSearchFacetsState } from "./SearchFacetsContext";

const STORAGE_NAME: string = "tm_search_history";
const MAX_HISTORY_ENTRIES: number = 5;

// Define default context value
const defaultContextValue: SearchHistoryContextType = {
    searchHistory: [],
    addHistory: () => {}
};

// Create the context
const SearchHistoryContext = createContext<SearchHistoryContextType>(defaultContextValue);

/**
 * Returns a comma-separated string of labels corresponding to the selected values.
 */
export const getLabels = (
    selected: string[],
    target: GroupListItem[]
) => target.filter((x) => selected.includes(x.value)).map((x) => x.label).join(" • ");

/**
 * Stores the search history in the local storage and triggers a storage event.
 * Logs an error message if the search history retrieval fails.
 */
export const setLocalHistory = (searchHistory: SearchHistoryEntry[]) => {
    const { publicRuntimeConfig: { appName } } = getConfig();
    try {
        localStorage.setItem(STORAGE_NAME, JSON.stringify(searchHistory));
        window.dispatchEvent(new Event("storage"));
    } catch (error) {
        const msg = `${appName} was unable to store the users search history: ${JSON.stringify(searchHistory)}`;
        console.error(msg, JSON.stringify(formatLog({ error })));
    }
};

/**
 * Retrieves the user's search history from local storage.
 * Logs an error message if the search history retrieval fails.
 */
export const getLocalHistory = (): SearchHistoryEntry[] => {
    const { publicRuntimeConfig: { appName } } = getConfig();
    let result: SearchHistoryEntry[] = [];
    try {
        result = JSON.parse(localStorage.getItem(STORAGE_NAME));
    } catch (error) {
        const msg = `${appName} was unable to retrieve the users search history`;
        console.error(msg, JSON.stringify(formatLog({ error })));
    }

    return Array.isArray(result) ? result : [];
};

/**
 * Builds a string array containing various search parameters and their corresponding labels.
 * If a parameter is not selected, its label is set to null.
 */
export const getArrayOfSearchFilters = ({
    keyword,
    company,
    location,
    filters,
    companyFacets
}) => {
    const keywordLabel = keyword?.userInputValue === keyword?.userSelectedKeyword?.label
        ? keyword?.userSelectedKeyword?.label
        : keyword?.userInputValue;
    const perks = companyFacets.perks_and_benefits.map((x) => x.items).flat();
    const searchFilters = [
        keywordLabel,
        company?.userSelectedCompany?.label ?? null,
        location?.userSelectedLocation?.value ?? null,
        getLabels(filters.category, jobCategoryItems),
        getLabels(filters.industry, companyIndustryItems),
        getLabels(filters.experienceLevel, experienceLevelItems),
        getLabels(filters.perks, perks),
        getLabels(filters.companySize, companySizeItems),
        getLabels(filters.companyLeadership, diversityInclusionItems),
        searchRadiusItems.find((x) => x.value === filters.radius)?.label ?? null,
        postedDateRangeItems.find((x) => x.value === filters.postedDateRange)?.label ?? null,
    ];

    return searchFilters
        .filter(Boolean)
        // remove defaults that are for display only
        .filter((x) => !["Anywhere", "Anytime"].includes(x))
        // Split combined filters
        .map((x) => x.split(" • "))
        // convert back to a single array.
        .flat()
        // remove leading and trailing spaces
        .map((x) => x.trim())
        // Remove empties
        .filter(Boolean);
};

export interface IGenerateNewHistoryProps {
    companyShortName: string,
    locationName: string,
    searchKeyword: string,
    filters: SearchFilters,
    asPath: string,
    setSearchHistory: Dispatch<SetStateAction<SearchHistoryEntry[]>>,
    keyword: SearchKeyword,
    company: SearchCompany,
    location: SearchLocation,
    // eslint-disable-next-line camelcase
    companyFacets: { perks_and_benefits?: PerkAndBenefitCategory; }
}

/**
 * Saves search history to localStorage and localState.
 */
export const generateNewHistory = (props: IGenerateNewHistoryProps) => {
    const {
        companyShortName,
        locationName,
        searchKeyword,
        filters,
        asPath,
        setSearchHistory,
        keyword,
        company,
        location,
        companyFacets
    } = props;
    const searchFilters = getArrayOfSearchFilters({
        keyword,
        company,
        location,
        filters,
        companyFacets
    });

    // generate the path, the async nature of populating the URL means we cannot trust `window.location.pathname`.
    const path = generateUrlPath({
        companyShortName,
        locationName: normalizeLocationName(locationName),
        searchKeyword,
        filters,
        asPath,
    });

    // Reasons to not generate a history
    const allowNewHistory = path
        && !["/search/", "/hiring/"].includes(path)
        && searchFilters.length
        && searchFilters.join(" • ");

    // Generate a history item for the current search.
    const currentSearch: SearchHistoryEntry[] = allowNewHistory
        ? [{ path, text: searchFilters.join(" • "), filters: searchFilters.length }]
        : [];

    // LocalStorage history minus the current search.
    const filteredHistory: SearchHistoryEntry[] = getLocalHistory()
        .filter((x: SearchHistoryEntry) => x.path !== path);

    // Only return our maxim history
    const results: SearchHistoryEntry[] = [...currentSearch, ...filteredHistory].flat().slice(0, MAX_HISTORY_ENTRIES);

    // push to local state
    setSearchHistory(results);
    // push to local storage
    setLocalHistory(results);
};

/**
 * Provides a context for managing search history entries.
 */
export const SearchHistoryProvider: FC<{ children: ReactNode; }> = ({ children }) => {
    const router = useRouter() || { asPath: "" };
    const { asPath } = router;
    const [searchHistory, setSearchHistory] = useState<SearchHistoryEntry[]>([]);
    const { companyFacets } = useSearchFacetsState();
    const searchState = useSearchState();
    const {
        company,
        location,
        keyword,
        filters,
        locationName,
        companyShortName,
        searchKeyword,
    } = searchResultsSelector(searchState);

    const updateLocalState = () => (event?: StorageEvent) => {
        // we only care about these specific storage changes.
        if (event && event.key !== STORAGE_NAME) return;
        const history = getLocalHistory();
        setSearchHistory(history);
    };

    useEffect(() => {
        addHistory();
    }, []);

    // Watch for changes to local storage.
    // Inject the current pages history into local storage
    useEffect(() => {
        window.addEventListener("storage", updateLocalState);
        return () => {
            window.removeEventListener("storage", updateLocalState);
        };
    }, []);

    const addHistory = () => generateNewHistory({
        companyShortName,
        locationName,
        searchKeyword,
        filters,
        asPath,
        setSearchHistory,
        keyword,
        company,
        location,
        companyFacets
    });

    return (
        <SearchHistoryContext.Provider value={{ searchHistory, addHistory }}>
            {children}
        </SearchHistoryContext.Provider>
    );
};
SearchHistoryProvider.displayName = "SearchHistoryProvider";

// Custom hook for using the SearchHistory context.
export const useSearchHistory = () => {
    const context = useContext(SearchHistoryContext);
    if (context === undefined) {
        throw new Error("useSearchHistory must be used within the SearchProvider");
    }
    return context;
};
